import React from "react";

/**
 * AppIcon component for displaying application icons with navigation functionality
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.app - The application data object
 * @param {string} props.app.label - The text label for the application
 * @param {Function} props.app.icon - React component that renders the icon
 * @param {string} props.app.href - URL or path for navigation (prefixed with '/app' for internal routes)
 * @param {Object} [props.app.notification] - Optional notification badge data
 * @param {number} [props.app.notification.count] - The count to display in the notification badge
 * @param {string} [props.app.notification.color] - The background color of the notification badge
 * @returns {JSX.Element} - Interactive application icon with navigation capabilities
 *
 * @example
 * // Basic usage with internal navigation
 * <AppIcon app={{
 *   label: "Dashboard",
 *   icon: DashboardIcon,
 *   href: "/app/dashboard"
 * }} />
 *
 * @example
 * // With notification badge
 * <AppIcon app={{
 *   label: "Messages",
 *   icon: MessageIcon,
 *   href: "/app/messages",
 *   notification: { count: 5, color: "#bf2e2e" }
 * }} />
 */
const AppIcon = ({ app }) => {
  const { notification } = app;

  const IconComponent = app.icon;

  return (
    <div className="group cursor-pointer flex flex-col items-center justify-center bg-white rounded-lg shadow p-6 hover:text-emerald-800 hover:shadow-lg transition-colors min-h-[120px] relative">
      <div className="group-hover:scale-110 group-hover:shadow-md group-hover:shadow-gray-300 ease-in-out duration-200 rounded-xl p-4 mb-4 bg-emerald-900 h-14 w-14 flex items-center justify-center">
        <span className="text-white text-3xl">
          {IconComponent && <IconComponent />}
        </span>
      </div>
      <span className="font-semibold text-lg text-center">{app.label}</span>
      {notification && (
        <span
          className={`absolute top-2 right-2 bg-red-900 text-white rounded-full px-2 py-1 text-xs font-bold ${
            notification.count > 0 ? "animate-bounce" : ""
          }`}
          style={{ backgroundColor: notification.color }}
        >
          {notification.count}
        </span>
      )}
    </div>
  );
};

export default AppIcon;
